import { FC, useEffect } from 'react';
import { useDebounce, sendGAEvent } from 'utils/utils';
import { useParams } from 'react-router-dom';

import { IdParamsType } from 'utils/types';
import { NoEventsFallback } from 'features/common/components/NoEventsFallback';
import { PunchupColumnPicker } from 'features/common/components/Punchup/composites/tables/PunchupColumnPicker';
import { PunchupOrdersTable } from './components/OrdersTable/PunchupOrdersTable';
import { PunchupSearch } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupSearch';
import { PunchupErrorState } from 'features/common/components/Punchup/primitives/feedback/PunchupErrorState';
import { PunchupTablePage } from 'features/common/components/Punchup/pages/PunchupTablePage';
import { isBackgroundRefetch } from 'features/common/utils/queryStatus';
import { PunchupTablePageHeader } from 'features/common/components/Punchup/composites/tables/PunchupTablePageHeader';
import { COLUMN_DEFS } from './components/OrdersTable/constants';
import { useColumnSelection } from 'features/common/hooks/useColumnSelection';
import { QueryResultProps } from './types';
import { getData } from 'services/api';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useOrdersPage } from './hooks/useOrdersPage';
import { useTranslation } from 'react-i18next';
import { postToast } from 'features/common/utils/postFlash';

const RESULTS_PER_PAGE = 30;

export const PunchupOrders: FC = () => {
	const { t } = useTranslation();
	const { id: eventId } = useParams<IdParamsType>();
	const queryClient = useQueryClient();

	const {
		searchText,
		setSearchText,
		selectedPeriod,
		selectedEvents,
		eventFilterApplied,
		isNotFiltered,
		downloadOrdersQuery,
	} = useOrdersPage(eventId);

	const { visibleDefs, visibleKeys, gridColumns, toggleColumn, allDefs, setColumnWidth } = useColumnSelection(
		COLUMN_DEFS,
		false
	);

	const searchDebounce = useDebounce(setSearchText);

	const ordersQuery = useInfiniteQuery({
		queryKey: ['orders', selectedPeriod.applied, selectedEvents.applied, searchText],
		queryFn: ({ pageParam = 1 }) => {
			const event_ids = eventId ? eventId : eventFilterApplied;

			const searchParams = new URLSearchParams();
			searchParams.set('page', pageParam.toString());
			searchParams.set('per_page', RESULTS_PER_PAGE.toString());

			searchText && searchParams.set('search', searchText);
			event_ids && searchParams.set('event_ids', event_ids);
			selectedPeriod.applied && searchParams.set('date', selectedPeriod.applied);

			return getData('/orders?' + searchParams.toString());
		},
		getNextPageParam: (lastPage, pages) => (lastPage.data.length >= RESULTS_PER_PAGE ? pages.length + 1 : undefined),
	});
	const ordersQueryObject = ordersQuery as QueryResultProps;

	const displayFallback =
		!ordersQueryObject.isLoading && isNotFiltered && !ordersQueryObject.data?.pages[0].data && searchText === '';

	// On a failed FIRST load react-query leaves `data` undefined, so the table body would otherwise spin
	// forever — surface a persistent, retryable error panel instead. A background-refetch error (data
	// already present) keeps the stale table + the toast below, rather than blowing the table away.
	const errorFallback =
		ordersQuery.isError && !ordersQueryObject.data ? (
			<PunchupErrorState
				title={t('orders-load-error-title', "We couldn't load transactions")}
				description={t('orders-load-error-description', "Something went wrong fetching this show's transactions.")}
				onRetry={() => ordersQuery.refetch()}
				retryLabel={t('button-try-again', 'Try again')}
			/>
		) : undefined;

	const downloadClick = () => {
		downloadOrdersQuery.mutate();
		sendGAEvent({ category: 'Orders', action: 'Orders download' });
	};

	const handleRefresh = () => {
		queryClient.refetchQueries(['orders']);
		sendGAEvent({ category: 'Orders', action: 'Orders refresh' });
	};

	useEffect(() => {
		if (ordersQuery.isError) postToast('error', t('orders-load-error', 'Failed to load transactions'));
	}, [ordersQuery.isError]);

	useEffect(() => {
		sendGAEvent({ category: 'Orders', action: 'Orders filter' });
	}, [selectedEvents, selectedPeriod]);

	return (
		<PunchupTablePage
			pageName="Transactions"
			header={
				<PunchupTablePageHeader
					title="Transactions"
					description="View and download all ticket sales for this show."
					onDownload={downloadClick}
					downloadLabel={t('ordersPage-download')}
					isDownloading={downloadOrdersQuery.isLoading}
					onRefresh={handleRefresh}
					refreshLabel={t('button-refresh', 'Refresh')}
					isRefreshing={ordersQueryObject.isFetching}
				/>
			}
			search={
				<PunchupSearch onChange={(e) => searchDebounce(e.target.value)} placeholder={t('search-email-or-name')} />
			}
			controlsActions={<PunchupColumnPicker allDefs={allDefs} visibleKeys={visibleKeys} onToggle={toggleColumn} />}
			pageFallback={errorFallback ?? (displayFallback ? <NoEventsFallback isFanDatabase={false} /> : undefined)}
		>
			<PunchupOrdersTable
				ordersQueryObject={ordersQueryObject}
				visibleDefs={visibleDefs}
				gridColumns={gridColumns}
				onResizeColumn={setColumnWidth}
				isLoading={ordersQueryObject.isLoading}
				isFetchingNextPage={ordersQueryObject.isFetchingNextPage}
				isRefetching={isBackgroundRefetch(ordersQueryObject)}
				hasActiveQuery={!isNotFiltered || searchText !== ''}
			/>
		</PunchupTablePage>
	);
};
